import React from 'react';
import { useI18n } from '../i18n';
import { WaitlistForm } from './WaitlistForm';
import { Cpu, Lock, ArrowUpRight, Zap, Github } from 'lucide-react';
import { trackOutboundClick } from '../lib/analytics';

export const Hero: React.FC = () => {
  const { t } = useI18n();

  return (
    <section id="waitlist-section" className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden bg-grid-pattern">
      {/* Background glow flares */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-gold-500/10 rounded-full blur-[120px] pointer-events-none -z-10" />
      <div className="absolute top-1/3 left-1/4 w-[400px] h-[300px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badges: Monthly Status & Open Source */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-navy-850 border border-slate-300 dark:border-gold-500/30 text-xs font-semibold text-slate-800 dark:text-gold-400 shadow-sm animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-amber-500 dark:bg-gold-500 animate-ping" />
            <span>{t.hero.statusBadge}</span>
            <ArrowUpRight className="w-3.5 h-3.5 opacity-70" />
          </div>

          <a
            href="https://github.com/workouse"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackOutboundClick('github', 'https://github.com/workouse')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-navy-850 border border-slate-300 dark:border-navy-700 text-xs font-semibold text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400 transition-colors shadow-sm"
          >
            <Github className="w-3.5 h-3.5" />
            <span>{t.hero.openSourceBadge}</span>
          </a>
        </div>

        {/* Main Headline with high contrast */}
        <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white max-w-5xl mx-auto leading-[1.15] mb-6">
          <span>{t.hero.titleLine1} </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-amber-600 to-yellow-600 dark:from-gold-300 dark:via-gold-400 dark:to-amber-500">
            {t.hero.titleHighlight}
          </span>
          <br className="hidden sm:inline" />
          <span> {t.hero.titleLine2}</span>
        </h1>

        {/* Subtitle / Description */}
        <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed font-normal">
          {t.hero.description}
        </p>

        {/* Primary Waitlist Signup Form */}
        <div className="mb-10">
          <WaitlistForm source="hero_main" />
        </div>

        {/* Trust Badges / Quick Specs */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 pt-6 border-t border-slate-200 dark:border-navy-800/80 max-w-4xl mx-auto text-xs sm:text-sm text-slate-700 dark:text-slate-300">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600 dark:text-gold-400 shrink-0" />
            <span className="font-semibold">{t.hero.trustPoint1}</span>
          </div>
          <div className="flex items-center gap-2">
            <Cpu className="w-4 h-4 text-amber-600 dark:text-gold-400 shrink-0" />
            <span className="font-semibold">{t.hero.trustPoint2}</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600 dark:text-gold-400 shrink-0" />
            <span className="font-semibold">{t.hero.trustPoint3}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
